import { Component, Input, OnInit, OnDestroy, LOCALE_ID, Inject } from '@angular/core';
import { BleachrLinkGenerator } from 'src/app/_classes';
import { Court, DataObjects, EnhancedArticle, Faceoff, GolfPlayer, Pagination } from 'src/app/_interfaces';
import { EnhancedArticlesComponent } from 'src/app/_modules/enhanced-articles/enhanced-articles.component';
import { AnalyticsService, AppStringsService, EnhancedArticlesService, MatchService, RouteParserService, TennisPlayersService } from 'src/app/_services';
import { SplideConfig } from '../../../_modules/carousel/splide-config';
import { MatDialog } from '@angular/material/dialog';
import { DatePipe, KeyValuePipe } from '@angular/common';
import { TabGroupComponent } from '../../../_modules/tab-group/tab-group.component';
import { TabComponent } from '../../../_modules/tab-group/tab/tab.component';
import { TabLabelComponent } from '../../../_modules/tab-group/tab/tab-label/tab-label.component';
import { TabContentComponent } from '../../../_modules/tab-group/tab/tab-content/tab-content.component';
import { SponsorBannerComponent } from '../../../_modules/sponsor-banner/sponsor-banner.component';
import { ImageComponent } from '../../../_modules/image/image.component';
import { handleBackgroundURL } from 'src/app/_functions';
import { SsrlocalstoreService } from 'src/app/_services/ssr-localstore.service';

export type DetailKeyName = 'birthplace' | 'birthdate' | 'height' | 'weight' | 'status' | 'pro' | 'amateur'

interface PlayerTab {
    type: string;
    label: string;
    event: string;
}

@Component({
    selector: 'app-player-detail',
    templateUrl: './player-detail.component.html',
    styleUrls: ['./player-detail.component.scss', '../player-dialog.component.scss'],
    imports: [TabGroupComponent, TabComponent, TabLabelComponent, TabContentComponent, SponsorBannerComponent, ImageComponent, DatePipe, KeyValuePipe]
})
export class PlayerDetailComponent implements OnInit, OnDestroy {
    @Input() player: GolfPlayer = {} as GolfPlayer;
    public articles: EnhancedArticle[] = [];
    public social_feed: DataObjects = {} as DataObjects;
    public is_loaded: boolean = false;
    public faceoffs: { data: Faceoff[], pagination: Pagination } = {
        data: [],
        pagination: {
            page: 1,
            per_page: 10,
            total: 1,
            total_pages: 1
        }
    };
    public intersectionObserver: IntersectionObserver | null = null;
    public target: HTMLElement | null = null;
    public faceoffs_ready: boolean = true;

    public rankings_details_keys: Record<
        string,
        {
            items: { key: string; value: string; corresponding_key?: string }[];
            title: string;
        }
    > = {
        // 1) world_rankings
        world_rankings_rank: {
            items: [
                { key: 'golfranking.rank', value: 'world_rankings_rank' },
                { key: 'golfranking.points.avg', value: 'world_rankings_details', corresponding_key: 'average_points' }
            ],
            title: 'golfranking.world_rankings'
        },

        // 2) rolex_world_rankings
        rolex_world_rankings_rank: {
            items: [
                { key: 'golfranking.rank', value: 'rolex_world_rankings_rank' },
                { key: 'golfranking.points.avg', value: 'rolex_world_rankings_details', corresponding_key: 'average_points' }
            ],
            title: 'golfranking.rolex_world_rankings'
        },

        // 3) fed_ex_cup
        fed_ex_cup_rank: {
            items: [
                { key: 'golfranking.rank', value: 'fed_ex_cup_rank' },
                { key: 'golfranking.points', value: 'fed_ex_cup_details', corresponding_key: 'value' }
            ],
            title: 'golfranking.fed_ex_cup'
        },

        // 4) fed_ex_cup_regular_season_points
        fed_ex_cup_regular_season_points_rank: {
            items: [
                { key: 'golfranking.rank', value: 'fed_ex_cup_regular_season_points_rank' },
                { key: 'golfranking.points', value: 'fed_ex_cup_regular_season_points_details', corresponding_key: 'value' }
            ],
            title: 'golfranking.fed_ex_cup_regular_season_points'
        },

        // NOTE: This should be added back once FE returns this data correctly
        // 5) presidents_cup_international
        // presidents_cup_international_rank: {
        //     items: [
        //         { key: 'golfranking.rank', value: 'presidents_cup_international_rank' },
        //         { key: 'golfranking.points', value: 'presidents_cup_international_details', corresponding_key: 'value' }
        //     ],
        //     title: 'golfranking.presidents_cup_international'
        // },

        race_to_cme_globe_rank: {
            items: [
                { key: 'golfranking.rank', value: 'race_to_cme_globe_rank' },
                { key: 'golfranking.points', value: 'race_to_cme_globe_details', corresponding_key: 'value' }
            ],
            title: 'golfranking.race_to_cme_globe'
        },

        asian_tour_order_of_merit_rank: {
            items: [
                { key: 'golfranking.rank', value: 'asian_tour_order_of_merit_rank' },
                { key: 'golfranking.points', value: 'asian_tour_order_of_merit_details', corresponding_key: 'value' }
            ],
            title: 'golfranking.asian_tour_order_of_merit'
        },

        // 6) race_to_dubai
        race_to_dubai_rank: {
            items: [
                { key: 'golfranking.rank', value: 'race_to_dubai_rank' },
                { key: 'golfranking.points', value: 'race_to_dubai_details', corresponding_key: 'value' }
            ],
            title: 'golfranking.race_to_dubai'
        },

        // 7) road_to_mallorca
        road_to_mallorca_rank: {
            items: [
                { key: 'golfranking.rank', value: 'road_to_mallorca_rank' },
                { key: 'golfranking.points', value: 'road_to_mallorca_details', corresponding_key: 'value' }
            ],
            title: 'golfranking.road_to_mallorca'
        },

        let_tour_female_order_of_merit_rank: {
            items: [
                { key: 'golfranking.rank', value: 'let_tour_female_order_of_merit_rank' },
                { key: 'golfranking.points', value: 'let_tour_female_order_of_merit_details', corresponding_key: 'value' }
            ],
            title: 'golfranking.let_tour_female_order_of_merit'
        },

        // 8) pga_career_money_leaders
        pga_career_money_leaders_rank: {
            items: [
                { key: 'golfranking.rank', value: 'pga_career_money_leaders_rank' },
                { key: 'golfranking.money', value: 'pga_career_money_leaders_details', corresponding_key: 'value' }
            ],
            title: 'golfranking.pga_career_money_leaders'
        },

        // 9) champions_career_money_leaders
        champions_career_money_leaders_rank: {
            items: [
                { key: 'golfranking.rank', value: 'champions_career_money_leaders_rank' },
                { key: 'golfranking.money', value: 'champions_career_money_leaders_details', corresponding_key: 'value' }
            ],
            title: 'golfranking.champions_career_money_leaders'
        },

        // 10) lpga_career_money_leaders
        lpga_career_money_leaders_rank: {
            items: [
                { key: 'golfranking.rank', value: 'lpga_career_money_leaders_rank' },
                { key: 'golfranking.money', value: 'lpga_career_money_leaders_details', corresponding_key: 'value' }
            ],
            title: 'golfranking.lpga_career_money_leaders'
        }
    };

    public details_keys: { key: string, value: DetailKeyName, corresponding_key?: string }[] = [
        { key: 'golf.players.stat.birthplace', value: 'birthplace' },
        { key: 'golf.players.stat.birthdate', value: 'birthdate' },
        { key: 'golf.players.stat.height', value: 'height' },
        { key: 'golf.players.stat.weight', value: 'weight' },
        // { key: 'golf.players.stat.pro_status', value: 'status' },
        // TODO: Include them once we decide how to display them on the UI
        // { key: 'golf.players.stats.pro_status.pro', value: 'pro' },
        // { key: 'golf.players.stats.pro_status.amateur', value: 'amateur' },
    ];
    public rounds: { id: string, round: Court }[] = [];
    public faceoffsByTour: Record<string, Faceoff[]> = { '': [] };
    public sortedFaceoffsByTour: { name: string, faceoffs: Faceoff[] }[] = [];

    public tabs: PlayerTab[] = [
        {
            type: `stats`,
            label: `golf.players.tabs.stats.title`,
            event: `Player details: stats`
        }
    ];
    public readonly subject_type: string = 'golfer';
    public pagination: Pagination = {
        page: 1,
        per_page: 10,
        total: 1,
        total_pages: 1
    };
    private newsBusy = false;
    private newsFinished = false;

    constructor(
        private playerAPI: TennisPlayersService,
        private routeParser: RouteParserService,
        private dialog: MatDialog,
        private analyticsService: AnalyticsService,
        private matchService: MatchService,
        public appStringAPI: AppStringsService,
        private articleService: EnhancedArticlesService,
        private ssr: SsrlocalstoreService,
        // LOCALE_ID lets us format dates without navigator.language (SSR-safe)
        @Inject(LOCALE_ID) private localeId: string
    ) { }

    get shouldShowTabs(): boolean {
      return (this.tabs?.length ?? 0) > 1;
    }

    get statsTab(): PlayerTab | undefined {
      return this.tabs?.find(t => t.type === 'stats');
    }


    get hasRankings(): boolean {
        const rankings = this.player?.rankings ?? [];
        if (rankings.length > 0) return true;

        const legacyKeys = [
            'world_rankings_rank',
            'rolex_world_rankings_rank',
            'fed_ex_cup_rank',
            'fed_ex_cup_regular_season_points_rank',
            // NOTE: This should be added back once FE returns this data correctly
            // 'presidents_cup_international_rank',
            'race_to_cme_globe_rank',
            'asian_tour_order_of_merit_rank',
            'race_to_dubai_rank',
            'road_to_mallorca_rank',
            'let_tour_female_order_of_merit_rank',
            'pga_career_money_leaders_rank',
            'champions_career_money_leaders_rank',
            'lpga_career_money_leaders_rank'
        ];

        return legacyKeys.some((k) => !!(this.player)?.[k]);
    }

    async ngOnInit(): Promise<void> {
      // set is_loaded immediately so SSR can render the template safely
      this.is_loaded = true;

      await this.buildView();
    }

    ngOnDestroy(): void {
        if (this.intersectionObserver) {
            this.intersectionObserver?.disconnect();
        }
    }

    getFaceoffsByTour(): Record<string, Faceoff[]> {
        return this.faceoffs?.data?.reduce?.((acc, cur) => {
            if (cur?.tournament_name && acc?.[cur.tournament_name]) {
                acc[cur.tournament_name].push(cur);
            } else if (cur?.tournament_name) {
                acc[cur.tournament_name] = [cur];
            } else {
                return cur;
            }
            return acc;
        }, {}) ?? {};
    }

    sortFaceOff(): Faceoff[] {
        let latest_dates: Faceoff[] = [];
        Object.values(this.faceoffsByTour).forEach(tour => {
            tour.sort((a, b) => new Date(a.starts_at) < new Date(b.starts_at) ? 1 : -1);
            latest_dates = [...latest_dates, tour[0]]
        });
        return latest_dates.sort((a, b) => new Date(a.starts_at) < new Date(b.starts_at) ? 1 : -1);
    }

    matchRound(match_id: string): Court {
        return this.rounds?.find?.(({ id }) => id === match_id)?.round ?? {} as Court;
    }

    handleIntersectionObserver(): void {
        if (!this.ssr.isClientSide) return;
        if (typeof document === 'undefined') return;
        if (typeof MutationObserver === 'undefined') return;
        if (typeof IntersectionObserver === 'undefined') return;

        const mutationObserver = new MutationObserver((mutations, obs) => {
            this.target = document.getElementById('last-section') as HTMLElement;
            if (this.target) {
                const observerOptions = {
                    root: null,
                    rootMargin: '0px',
                    threshold: [0.0, 0.75]
                };

                this.intersectionObserver = new IntersectionObserver((callback) => {
                    this.handleCallback(callback);
                }, observerOptions);

                this.intersectionObserver.observe(this.target);
                obs.disconnect();
                return;
            }
        });

        mutationObserver.observe(document, {
            childList: true,
            subtree: true
        });
    }

    handleCallback(callbacks: IntersectionObserverEntry[]): void {
        callbacks.forEach(async (entry: IntersectionObserverEntry) => {
            const isIntersecting = entry?.isIntersecting && entry.target === this.target;

            if (isIntersecting && this.faceoffs_ready && this.faceoffs.data.length < this.faceoffs.pagination.total) {
                await this.getPlayersFaceoffs(this.faceoffs.pagination.page + 1);
            } else if (this.faceoffs.data.length === this.faceoffs.pagination.total) this.faceoffs_ready = true;
        });
    }

    async postAnalytistTab(screen_name: string): Promise<void> {
        const event = {
            screen_name,
            parameters: {
                player_id: this.player.id
            }
        }
        await this.analyticsService.postEvent('page_view', event);
    }

    async postInitAnalyist(): Promise<void> {
        const event = {
            screen_name: 'Player details',
            parameters: {
                player_id: this.player.id
            }
        };
        await this.analyticsService.postEvent('page_view', event);
        await this.postAnalytistTab('Player details: stats')
        if (this.hasRankings) {
            await this.postAnalytistTab('Player details: ranking')
        }
    }

    async buildView(): Promise<void> {
        this.is_loaded = true;
        await this.postInitAnalyist();

        if (this.player?.id && this.subject_type) {
            await this.buildArticleSubjectView();
        }

        if (this.articles?.length) {
            this.tabs.push({
                type: `news`,
                label: `golf.players.tabs.news.title`,
                event: `Player details: news`
            })
        }

        // Add this if we move rankings to a separate tab
        // if (this.hasRankings) {
        //     this.tabs.push({
        //         type: `ranking`,
        //         label: `golf.players.tabs.rankings.title`,
        //         event: `Player details: ranking`
        //     })
        // }
    }

    async getPlayersFaceoffs(page?: number): Promise<void> {
        if (this.faceoffs?.pagination?.total) {
            this.faceoffs_ready = false;
            const faceoffs = await this.playerAPI.getPlayersFaceoffs(this.player.id, page ? page : this.faceoffs.pagination?.page ?? 1);
            const faceoffsIds = (faceoffs?.data?.map?.(v => v.id) ?? [])
            if (faceoffsIds) {
                this.rounds = [
                    ...this.rounds,
                    ...await this.matchService.getMatchesRoundsByIds(faceoffsIds)
                ];
            }
            this.faceoffs = {
                data: [...this.faceoffs.data, ...faceoffs.data],
                pagination: faceoffs?.pagination ? faceoffs?.pagination : this.faceoffs.pagination
            };
            this.faceoffsByTour = this.getFaceoffsByTour();
            this.sortedFaceoffsByTour = this.sortFaceOff().map(faceoff => {
                return { name: faceoff.tournament_name, faceoffs: [...this.faceoffsByTour[faceoff.tournament_name]] };
            });
            this.faceoffs_ready = true;
        }
    }

    playerHeight() {
        if (this.player.height?.metric && !this.player.height.metric.includes('cm')) {
            const height = Number(this.player.height.metric.replace(/ /g, '').split('m')[0]) * 100;
            this.player.height.metric = `${height}cm`
        }
        if (this.player?.height?.imperial && this.player.height.metric) {
            return `${this.player.height.imperial} - ${this.player.height.metric}`;
        }
        else if (!this.player?.height?.imperial && this.player?.height?.metric) {
            return `${this.player.height.metric}`;
        }
        else if (this.player.height?.imperial && !this.player?.height?.metric) {
            return `${this.player.height.imperial}`;
        }
        else return '-';
    }

    getDetail(key: string): string {
        if (!this.player || Object.keys(this.player).length < 1) {
            return '-';
        }

        switch (key) {
            case 'height': {
                return this.playerHeight();
            }
            case 'birthplace': {
                return this.isBottomValue(this.player.birth_place);
            }
            case 'birthdate': {
                const rawDate = this.player.birth_date;
                if (!rawDate) return '-';
                try {
                    const date = new Date(rawDate);

                    // removed navigator.language (SSR crash)
                    // SSR has no "navigator", so we use Angular LOCALE_ID which works on server & browser.
                    const locale = this.localeId || 'en-US';

                    const formatted = new Intl.DateTimeFormat(locale, {
                        year: '2-digit',
                        month: '2-digit',
                        day: '2-digit'
                    }).format(date);
                    return formatted;
                } catch {
                    return '-';
                }
            }
            case 'status': {
                const status = this.isBottomValue(this.player.status);
                return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
            }
            default: {
                return '-';
            }
        }
    }


    isBottomValue(value: string) {
        if (!value) {
            return `-`;
        }
        if ((value === `0`) || (value === `0-0`)) {
            return `-`;
        }
        return value
    }

    handleBackground(image_url: string): string {
        return handleBackgroundURL(image_url);
    }

    get splideConfig() {
        return new SplideConfig().options({
            type: 'slide',
            perPage: 1.2,
            gap: '1.2rem',
            padding: '0rem',
            focus: 'center',
            trimSpace: true,
            drag: true,
            arrows: true,
            pagination: false,
            fixedHeight: '250px',
            autoWidth: true
        })
    }

    async openLink(item: EnhancedArticle) {
        if (!this.ssr.isClientSide) return;
        
        const url = new BleachrLinkGenerator().enhancedArticle(item.id)

        const iframe_data = await this.routeParser.parseBleachrRoutes(url)

        if (iframe_data) {
            const view_payload = {
                article_id: item.id,
                contributor_id: item.contributor.id,
                viewed_from_source: 'player_details',
                source: { tennis_player_id: this.player.id }
            };
            this.analyticsService.postEvent('article_viewed', view_payload);
            this.dialog.open(EnhancedArticlesComponent, {
                data: { iframe_data, card: item },
                width: '60vw',
                height: '90vh'
            });
        }
    }

    returnZero() {
        return 0;
    }

    getRankingTitle(title: any): string {
        return title as string
    }

    getRankingItems(items: any): { key: string, value: string, corresponding_key?: string }[] {
        return items as { key: string, value: string, corresponding_key?: string }[]
    }

    asIsOrder(a: any, b: any): number {
        return 0;
    }

    getRanking(key: string) {
        if (this.player?.rankings?.length) {
            return this.player.rankings.find(({ type }) => key.startsWith(type));
        }

        return (this.player)?.[key];
    }

    getRankingValue(ranking: { key: string; value: string; corresponding_key?: string }) {
        if (this.player?.rankings?.length) {
            const found = this.player.rankings.find(({ type }) => ranking?.value.startsWith(type));

            if (ranking.key.includes('.rank')) {
                return found?.rank ?? '-';
            }

            if (ranking?.corresponding_key) {
                if (found?.details?.[ranking.corresponding_key]) {
                  return this.formatPoints(found?.details?.[ranking.corresponding_key], ranking?.key)
                } 
                return '-';
            }

            return '-';
        }

        if (ranking?.corresponding_key) {
            return (this.player)?.[ranking.value]?.[ranking.corresponding_key] ?? '-';
        }
        return (this.player)?.[ranking.value] ?? '-';
    }

    private formatPoints(v: any, rank_filter: string): string {
      const n = typeof v === 'number' ? v : Number(v);
      const isAvg =
        rank_filter === 'world_rankings_rank' ||
        rank_filter === 'rolex_world_rankings_rank';

      if (Number.isFinite(n) && isAvg) return String(Number(n.toFixed(4)));

      if (Number.isFinite(n)) return String(n);

      return String(v);
    }

    async buildArticleSubjectView(): Promise<void> {
        if (this.pagination.total) {
            try {
                const articles = await this.articleService.getEnhancedArticles(this.pagination.page, this.subject_type, this.player.id);

                this.articles = [...this.articles, ...(articles?.data ?? [])];
                this.pagination = articles?.meta?.pagination ? articles.meta.pagination : this.pagination;
            } catch (e) {
                this.articles = [];
            }
        }
    }

    async tabChanged({ index }: { index: number }): Promise<void> {
        if (index && this.tabs?.[index]?.type === 'news') {
            await this.postAnalytistTab('Player details: news')
        }
    }

    handleType(type: string) {
        const tidy_text = type.toUpperCase().replace("_", " ");
        return type === 'videos' ? tidy_text.substring(0, (type.length - 1)) : tidy_text;
    }

    onNewsScroll(evt: Event): void {
        if (this.newsBusy || this.newsFinished) { return; }
        const el = evt.target as HTMLElement;
        const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 20;
        if (atBottom) { this.loadNextNewsPage(); }
    }

    private async loadNextNewsPage(): Promise<void> {
        if (this.pagination.page >= this.pagination.total_pages) {
            this.newsFinished = true;
            return;
        }
        this.newsBusy = true;
        this.pagination.page++;
        await this.buildArticleSubjectView();
        this.newsBusy = false;
        if (this.pagination.page >= this.pagination.total_pages) {
            this.newsFinished = true;
        }
    }

    anyValueToString(item: any): string {
      return item ?? ''
    }
}
